import { useCallback } from 'react';

import { getSaladAiSurvey, submitSaladAiAnswers } from '@lib/api/SaladAiService';
import { GENERIC_ERROR_MESSAGE } from '@lib/constants/Strings';
import useApplicationState, { ApplicationState } from '@lib/state';
import { SaladAiSurvey } from '@lib/entities/SaladAi';
import Storage from '@lib/utils/storage';

import useToast from './useToast';

const SALAD_AI_HIDE_WELCOME_BANNER_KEY = 'salad_ai_hide_welcome_banner';

const selectSaladAi = (s: ApplicationState) => s.saladAi;

const DEFAULT_VISIBILITY = true;

const useSaladAI = () => {
    const state = useApplicationState(selectSaladAi);
    const { toast } = useToast();
    const {
        survey,
        answers,
        recommendations,
        surveyLoading,
        surveyError,
        submitLoading,
        submitError,
        setSurvey,
        answerQuestion,
        answerQuestionWithNoneSelected,
        setRecommendations,
        setSurveyLoading,
        setSurveyError,
        setSubmitLoading,
        setSubmitError
    } = state;

    const fetchSurvey = useCallback(async () => {
        try {
            setSurveyLoading(true);
            setSurveyError(null);
            const data = await getSaladAiSurvey();
            if (data && !('isApiError' in data)) {
                setSurvey(data as SaladAiSurvey);
            }
        } catch (err) {
            setSurveyError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setSurveyLoading(false);
        }
    }, [setSurveyError, setSurvey, setSurveyLoading]);

    const submitAnswers = useCallback(async () => {
        if (answers.every((answer) => answer.selections.length === 0)) {
            toast({
                message: 'Please select at least one option to view recommendations',
                level: 'error'
            });
            return;
        }
        try {
            setSubmitLoading(true);
            setSubmitError(null);
            const data = await submitSaladAiAnswers(answers);
            if ('error' in data) {
                setSubmitError(data.error);
                toast({
                    message: GENERIC_ERROR_MESSAGE,
                    level: 'error'
                });
            } else {
                setRecommendations(data);
            }
        } catch (err) {
            toast({
                message: GENERIC_ERROR_MESSAGE,
                level: 'error'
            });
            setSubmitError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setSubmitLoading(false);
        }
    }, [answers, setRecommendations, setSubmitError, setSubmitLoading]);

    const showWelcomeBanner = async (): Promise<boolean> => {
        const visibility = await Storage.getItemAsync(SALAD_AI_HIDE_WELCOME_BANNER_KEY);
        return visibility === null ? DEFAULT_VISIBILITY : !JSON.parse(visibility);
    };

    const setWelcomeBannerVisibilityData = (value: boolean): void => {
        Storage.setItemAsync(SALAD_AI_HIDE_WELCOME_BANNER_KEY, JSON.stringify(value));
    };

    const hideWelcomeBanner = (): void => {
        setWelcomeBannerVisibilityData(true);
    };

    return {
        ...state,
        fetchSurvey,
        showWelcomeBanner,
        hideWelcomeBanner,
        survey,
        surveyError,
        surveyLoading,
        answerQuestion,
        submitAnswers,
        submitLoading,
        submitError,
        recommendations,
        answers,
        answerQuestionWithNoneSelected
    };
};

export default useSaladAI;
